import * as Yup from 'yup';

import {
  BoxLayout,
  DialogLayout,
  FormLoadingButton,
  FormSearchableSelectField,
  NewConvergeButton,
} from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  SubmissionStatus,
  SubmissionSubStatus,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';

import { ComponentProps } from 'react';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { useConvergeSnackBar } from '../../../contexts';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { yupResolver } from '@hookform/resolvers/yup';

interface IDeclineSubmissionButtonProps
  extends ComponentProps<typeof LoadingButton> {
  referenceId?: string;
  submissionId: string;
}
interface IDeclineFormFelids {
  reason: SubmissionSubStatus;
}
export const DeclineSubmissionButton = ({
  referenceId,
  submissionId,
  ...props
}: IDeclineSubmissionButtonProps) => {
  const {
    info: {
      allowDeclineSubmission,
      isDeclined,
      submissionSubStatus,
      declinedBecause,
      declinationReasons,
    },
  } = useUnderwritingSubmissionStateContext();

  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { upsertSubmission, loading: isUpdatingSubmission } =
    useUpsertSubmissionMutation({}, 'Overview');
  const [showDeclineConfirmationModal, setShowDeclineConfirmationModal] =
    useState<boolean>(false);
  const { t } = useTranslation();

  const verb = isDeclined ? 'undecline' : 'decline';
  const pastVerb = isDeclined ? 'undeclined' : 'declined';
  const activeVerb = isDeclined ? 'undeclining' : 'declining';
  const activeVerbAsSentenceCase = isDeclined ? 'Undeclining' : 'Declining';
  const verbAsSentenceCase = isDeclined ? 'Undecline' : 'Decline';

  const onDeclineSubmissionConfirmationEvent = useCallback(
    async (submissionUuid: string, subStatus: SubmissionSubStatus) => {
      closeSnackbar();
      setShowDeclineConfirmationModal(false);

      notify(`${activeVerbAsSentenceCase} submission...`, {
        variant: 'info',
      });

      try {
        await upsertSubmission({
          variables: {
            details: {
              uuid: submissionUuid,
              status: isDeclined
                ? SubmissionStatus.Evaluation
                : SubmissionStatus.Declined,
              subStatus: isDeclined
                ? SubmissionSubStatus.EvaluationEditable
                : subStatus,
            },
          },
        });
        closeSnackbar();
        notify(`Successfully ${pastVerb} submission`, {
          variant: 'success',
        });
      } catch (cause) {
        closeSnackbar();
        notify(
          cause instanceof Error
            ? `Error ${activeVerb}: ${cause.message}`
            : `Error ${activeVerb}`,
          {
            variant: 'error',
          }
        );
      }
    },
    [
      activeVerb,
      activeVerbAsSentenceCase,
      closeSnackbar,
      isDeclined,
      notify,
      pastVerb,
      upsertSubmission,
    ]
  );

  const formMethods = useForm<IDeclineFormFelids>({
    defaultValues: {
      reason: submissionSubStatus ?? undefined,
    },
    resolver: yupResolver(
      Yup.object().shape({
        reason: Yup.string()
          .typeError(
            t<string>(
              'submission.declineSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          )
          .required(
            t<string>(
              'submission.declineSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          )
          .trim()
          .min(
            1,
            t<string>(
              'submission.declineSubmission.requiredError',
              'Prior to confirming, choose a reason'
            )
          ),
      })
    ),
  });

  if (!allowDeclineSubmission && !isDeclined) {
    return null;
  }

  const tPrefix = `submission.${verb}Submission`;

  return (
    <>
      <NewConvergeButton
        {...props}
        tooltip={
          isDeclined
            ? declinedBecause
              ? `${declinedBecause} Click to undecline.`
              : `Undecline submission`
            : `Change submission status to declined`
        }
        loading={isUpdatingSubmission}
        variant="contained"
        onClick={() => setShowDeclineConfirmationModal(true)}
        label={isDeclined ? 'Undecline' : 'Decline'}
      />

      <DialogLayout
        id="decline-submission-dialog"
        open={showDeclineConfirmationModal}
        showCloseButton
        onCloseIconClick={() => setShowDeclineConfirmationModal(false)}
        onClose={() => setShowDeclineConfirmationModal(false)}
        maxWidth="lg"
        header={
          <>{t(`${tPrefix}.header`, `${verbAsSentenceCase} submission`)}</>
        }
        body={
          <>
            {declinedBecause && (
              <Typography component={'p'}>{declinedBecause}</Typography>
            )}

            <Typography component={'p'}>
              {t<string>(
                `${tPrefix}.bodyText`,
                `Are you sure you want to ${verb} the submission?`,
                {
                  referenceId,
                }
              )}
            </Typography>

            {isDeclined ? undefined : (
              <FormProvider {...formMethods}>
                <FormSearchableSelectField
                  minDropdownWidth="680px"
                  maxDropdownWidth="680px"
                  name="reason"
                  label="Reason"
                  containerWidth={4}
                  options={declinationReasons}
                  required
                  disabled={isUpdatingSubmission || isDeclined}
                />
              </FormProvider>
            )}
          </>
        }
        footer={
          <BoxLayout sx={{ m: 2 }}>
            <FormLoadingButton
              type="reset"
              onClick={() => setShowDeclineConfirmationModal(false)}
              sx={{ mr: 2 }}
              variant="text"
            >
              {t('submission.declineSubmission.cancel', 'Cancel')}
            </FormLoadingButton>
            <FormLoadingButton
              loading={isUpdatingSubmission}
              onClick={formMethods.handleSubmit((details) => {
                onDeclineSubmissionConfirmationEvent(
                  submissionId,
                  details.reason as unknown as SubmissionSubStatus
                );
              })}
            >
              {t('submission.declineSubmission.confirm', 'Confirm')}
            </FormLoadingButton>
          </BoxLayout>
        }
      />
    </>
  );
};
